import { useEffect, useState } from "react";

import Hls from "hls.js";

import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import NoRecordingCard from "@app/analytics/MeetingReport/components/NoRecordingCard";
import useMeetingReportInitialLoading from "@app/analytics/MeetingReport/hooks/useMeetingReportInitialLoading";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useVideoRecording from "@app/analytics/MeetingReport/hooks/useVideoRecording";
import { platformDoesNotSupportRecording } from "@app/analytics/MeetingReport/utils/videoUtils";
import PlaybackUnauthorized from "@app/analytics/MeetingReport/VideoPlayer/PlaybackUnauthorized";
import useUserMetadata from "@app/common/auth/useUserMetadata";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { MeetingPlatform } from "@app/common/types";

import Video from "./Video";

const VideoPlayer = ({
  platform,
  thumbnailUrl,
}: {
  platform: MeetingPlatform;
  thumbnailUrl?: string | null;
}) => {
  const { userMetadata } = useUserMetadata();

  const { isInitialLoading: meetingReportIsLoading } =
    useMeetingReportInitialLoading();
  const {
    data: {
      isRestrictedReport,
      currentUserCanUpgradeForReport,
      recordingEnabled,
      shouldUpsellRecordingEntities,
      uploadFileType,
    },
  } = useSessionMetadata();
  const { subscription } = useBillingContext();
  const { videoRecording, hasHighlightReel, hasTrailer, hasVideoRecording } =
    useVideoRecording({
      enabled: !isRestrictedReport,
    });
  const { canAccessPlayback } = useFeatureAccess();

  const [autoPlayVideo, setAutoPlayVideo] = useState<boolean | undefined>(
    userMetadata?.autoPlayMeetingReportVideoOnLoad
  );

  useEffect(() => {
    // only update the autoplay video state value if we're going from an unknown setting
    // state to a known setting state; other updates to the settings will be ignored,
    // since we only care about autoplay happening on initial page load
    if (
      autoPlayVideo == null &&
      userMetadata?.autoPlayMeetingReportVideoOnLoad != null
    ) {
      setAutoPlayVideo(userMetadata.autoPlayMeetingReportVideoOnLoad);
    }
  }, [autoPlayVideo, userMetadata?.autoPlayMeetingReportVideoOnLoad]);

  // show messaging that the user does not have access to playback via their workspace if their
  // billing plan would otherwise allow playback (i.e., is on enterprise plan). if the user doesn't
  // have access, that messaging takes top priority since whether the recording exists or not is
  // irrelevant; they won't be able to see it either way
  if (!canAccessPlayback && subscription.settings.storeRecording) {
    return <NoRecordingCard type="playback_disabled" />;
  }

  if (shouldUpsellRecordingEntities) {
    const type = currentUserCanUpgradeForReport
      ? "restricted_upsell"
      : "upsell";
    return <NoRecordingCard type={type} />;
  }

  if (!recordingEnabled) {
    // if recording wasn't enabled for the session, don't bother showing any other messaging besides just
    // that it doesn't exist
    return <NoRecordingCard type="default" />;
  }

  const noRecording = videoRecording == null || !hasVideoRecording;

  // if the meeting platform doesn't support recording, don't bother trying to upsell this session,
  // since upgrading won't have any effect
  if (noRecording && platformDoesNotSupportRecording(platform)) {
    return <NoRecordingCard type="unsupported_platform" />;
  }

  if (videoRecording?.status === "unauthorized") {
    return <PlaybackUnauthorized thumbnailUrl={thumbnailUrl} />;
  }

  if (noRecording) {
    const type = subscription.settings.storeRecording ? "default" : "upsell";

    return <NoRecordingCard type={type} />;
  }

  if (!Hls.isSupported()) {
    return <NoRecordingCard type="unsupported_browser" />;
  }

  return (
    <Video
      autoPlay={Boolean(autoPlayVideo)}
      hasHighlightReel={hasHighlightReel}
      hasTrailer={hasTrailer}
      isLoading={meetingReportIsLoading}
      videoRecording={videoRecording}
      thumbnailUrl={thumbnailUrl}
      uploadFileType={uploadFileType}
    />
  );
};

export default VideoPlayer;
